import { getAuthToken } from '../utils/authUtils';
import axios from 'axios';
import requestDeduplicationService from './requestDeduplicationService';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

// Simple in-memory cache for facility data
const facilityCache = new Map();
const FACILITY_CACHE_TTL = 60000; // 1 minute



export class FacilityService {
  static async getFacilitiesByAdmin(adminId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(`${API_BASE}/facility/facilities/admin/${adminId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.facilities || [];
    } catch (error) {
      console.error('Error fetching facilities by admin:', error);
      throw error;
    }
  }

  static async updateFacilityBranding(facilityId, brandingData) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.put(`${API_BASE}/facility/facilities/${facilityId}/branding`, brandingData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error updating facility branding:', error);
      throw error;
    }
  }

  static async uploadFacilityLogo(file) {
    try {
      const token = await getAuthToken();
      
      const formData = new FormData();
      formData.append('logo', file);

      const response = await axios.post(`${API_BASE}/facility/facilities/upload-logo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.logoUrl;
    } catch (error) {
      console.error('Error uploading facility logo:', error);
      throw error;
    }
  }

  static async updateMultipleFacilitiesBranding(facilityIds, brandingData) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.put(`${API_BASE}/facility/facilities/branding/batch`, {
        facilityIds,
        brandingData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error updating multiple facilities branding:', error);
      throw error;
    }
  }

  /**
   * Get facility by ID
   * @param {string} facilityId - The facility ID
   * @returns {Promise<Object>} Facility data
   */
  static async getFacilityById(facilityId) {
    try {
      // Check simple cache first
      const cacheKey = `facility_${facilityId}`;
      const cached = facilityCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < FACILITY_CACHE_TTL) {
        console.log('getFacilityById: Using cached facility data');
        return cached.data;
      }

      // Check deduplication service for pending requests
      const dedupeKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE}/facility/facilities/${facilityId}`, { facilityId });
      if (requestDeduplicationService.isRequestPending(dedupeKey)) {
        console.log('getFacilityById: Request already in progress, waiting...');
        return await requestDeduplicationService.trackRequest(dedupeKey);
      }

      const token = await getAuthToken();

      const response = await axios.get(`${API_BASE}/facility/facilities/${facilityId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const facility = response.data.facility;
      
      // Cache the result
      facilityCache.set(cacheKey, { data: facility, timestamp: Date.now() });
      requestDeduplicationService.completeRequest(dedupeKey, facility);

      return facility;
    } catch (error) {
      console.error('Error fetching facility:', error);
      throw error;
    }
  }

  /**
   * Get facility departments
   * @param {string} facilityId - The facility ID
   * @returns {Promise<Array>} Array of departments
   */
  static async getFacilityDepartments(facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(`${API_BASE}/facility/facilities/${facilityId}/departments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.departments || [];
    } catch (error) {
      console.error('Error fetching facility departments:', error);
      return [];
    }
  }

  /**
   * Get facility rooms
   * @param {string} facilityId - The facility ID
   * @param {string} [departmentId] - Optional department ID to filter rooms
   * @returns {Promise<Array>} Array of rooms
   */
  static async getFacilityRooms(facilityId, departmentId) {
    try {
      const token = await getAuthToken();
      
      const url = `${API_BASE}/facility/facilities/${facilityId}/rooms${departmentId ? `?departmentId=${departmentId}` : ''}`;
      console.log('Fetching rooms from:', url);
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.rooms || [];
    } catch (error) {
      console.error('Error fetching facility rooms:', error);
      return [];
    }
  }

  /**
   * Get all facilities (admin only)
   * @returns {Promise<Array>} Array of all facilities
   */
  static async getAllFacilities() {
    try {
      const token = await getAuthToken();
      
              const response = await axios.get(`${API_BASE}/facility/facilities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.facilities || [];
    } catch (error) {
      console.error('Error fetching all facilities:', error);
      throw error;
    }
  }

  /**
   * Get all facility groups (admin only)
   * @returns {Promise<Array>} Array of all facility groups
   */
  static async getAllFacilityGroups() {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(`${API_BASE}/facility/facility-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // API returns array directly, normalize facilities array for each group
      const groups = Array.isArray(response.data) ? response.data : [];
      return groups.map(group => ({
        ...group,
        facilities: Array.isArray(group.facilities) ? group.facilities : []
      }));
    } catch (error) {
      console.error('Error fetching all facility groups:', error);
      throw error;
    }
  }

  /**
   * Get facility group names by IDs
   * @param {Array<string>} groupIds - Array of facility group IDs
   * @returns {Promise<Array>} Array of facility group names
   */
  static async getFacilityGroupNames(groupIds) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(`${API_BASE}/facility/facility-groups/names`, {
        groupIds
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.groupNames || [];
    } catch (error) {
      console.error('Error fetching facility group names:', error);
      throw error;
    }
  }

  /**
   * Get facility names by IDs
   * @param {Array<string>} facilityIds - Array of facility IDs
   * @returns {Promise<Array>} Array of facility names
   */
  static async getFacilityNames(facilityIds) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(`${API_BASE}/facility/facilities/names`, {
        facilityIds
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.facilityNames || [];
    } catch (error) {
      console.error('Error fetching facility names:', error);
      throw error;
    }
  }

  /**
   * Update HISP configuration for a facility
   * @param {string} facilityId - The facility ID
   * @param {Object} config - The HISP configuration data
   * @returns {Promise<Object>} Update result
   */
  static async updateHispConfig(facilityId, config) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.put(`${API_BASE}/facility/facilities/${facilityId}/hisp-config`, config, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error updating HISP configuration:', error);
      throw error;
    }
  }

  /**
   * Get accessible facilities for a user
   * @param {boolean} isAdmin - Whether the user is an admin
   * @returns {Promise<Array>} Array of facility objects
   */
  static async getUserAccessibleFacilities(isAdmin) {
    try {
      const token = await getAuthToken();
      
      if (isAdmin) {
        // Admins can access all facilities
        const response = await axios.get(`${API_BASE}/facility/facilities`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data.facilities || [];
      } else {
        // Regular users can only access their assigned facilities
        const response = await axios.get(`${API_BASE}/internal/users/facilities`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data.facilities || [];
      }
    } catch (error) {
      console.error('Error fetching user accessible facilities:', error);
      throw error;
    }
  }

  /**
   * Get user's primary facility
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} User's primary facility
   */
  static async getUserFacility(userId) {
    try {
      const token = await getAuthToken();
      
      // Get user's facility assignments
      const response = await axios.get(`${API_BASE}/internal/users/${userId}/facility-assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const facilityIds = response.data.facilityIds || [];
      if (facilityIds.length === 0) {
        return null;
      }

      // Get the first facility (primary facility)
      const facilityResponse = await axios.get(`${API_BASE}/facility/facilities/${facilityIds[0]}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return facilityResponse.data.facility || null;
    } catch (error) {
      console.error('Error fetching user facility:', error);
      throw error;
    }
  }

  /**
   * Get facility group by ID
   * @param {string} groupId - The facility group ID
   * @returns {Promise<Object>} Facility group data
   */
  static async getFacilityGroup(groupId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(`${API_BASE}/facility/facility-groups/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.facilityGroup || null;
    } catch (error) {
      console.error('Error fetching facility group:', error);
      throw error;
    }
  }

  /**
   * Get facility by ID (alias for getFacilityById)
   * @param {string} facilityId - The facility ID
   * @returns {Promise<Object>} Facility data
   */
  static async getFacility(facilityId) {
    return FacilityService.getFacilityById(facilityId);
  }

  /**
   * Get facilities by IDs
   * @param {Array<string>} facilityIds - Array of facility IDs
   * @returns {Promise<Array>} Array of facility objects
   */
  static async getFacilitiesByIds(facilityIds) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(`${API_BASE}/facility/facilities/batch`, {
        facilityIds
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.facilities || [];
    } catch (error) {
      console.error('Error fetching facilities by IDs:', error);
      throw error;
    }
  }

  /**
   * Update facility
   * @param {string} facilityId - The facility ID
   * @param {Object} facilityData - The facility data to update
   * @returns {Promise<Object>} Update result
   */
  static async updateFacility(facilityId, facilityData) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.put(`${API_BASE}/facility/facilities/${facilityId}`, facilityData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error updating facility:', error);
      throw error;
    }
  }

  /**
   * Create a new facility
   * @param {Object} facilityData - The facility data to create
   * @returns {Promise<Object>} Created facility data
   */
  static async createFacility(facilityData) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(`${API_BASE}/facility/facilities`, facilityData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error creating facility:', error);
      throw error;
    }
  }
} 

// Named exports for commonly used methods
export const getAllFacilities = FacilityService.getAllFacilities;
export const getAllFacilityGroups = FacilityService.getAllFacilityGroups;
export const getFacilityGroupNames = FacilityService.getFacilityGroupNames;
export const getFacilityNames = FacilityService.getFacilityNames;
export const updateHispConfig = FacilityService.updateHispConfig;
export const getUserAccessibleFacilities = FacilityService.getUserAccessibleFacilities;
export const getFacilityById = FacilityService.getFacilityById;
export const getFacilityDepartments = FacilityService.getFacilityDepartments;
export const getFacilitiesByAdmin = FacilityService.getFacilitiesByAdmin;
export const updateFacilityBranding = FacilityService.updateFacilityBranding;
export const uploadFacilityLogo = FacilityService.uploadFacilityLogo;
export const updateMultipleFacilitiesBranding = FacilityService.updateMultipleFacilitiesBranding;
export const getUserFacility = FacilityService.getUserFacility;
export const getFacilityGroup = FacilityService.getFacilityGroup;
export const getFacility = FacilityService.getFacility;
export const getFacilitiesByIds = FacilityService.getFacilitiesByIds;
export const updateFacility = FacilityService.updateFacility;
export const createFacility = FacilityService.createFacility;